import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { contact } from "../data/contact";
import { useLanguage } from "../context/LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, toggleLanguage, dir } = useLanguage();

  const links = [
    { to: "/", key: "home", label: t.nav.home },
    { to: "/about", key: "about", label: t.nav.about },
    { to: "/services", key: "services", label: t.nav.services },
    { to: "/pricing", key: "pricing", label: t.nav.pricing },
    { to: "/demos", key: "demos", label: t.nav.demos },
    { to: "/contact", key: "contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      dir={dir}
      lang={lang}
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "py-3.5" : "py-6"
      }`}
      data-testid="main-navbar"
    >
      <div
        className={`mx-auto max-w-7xl px-5 sm:px-8 transition-all duration-500 ${
          scrolled ? "" : ""
        }`}
      >
        <nav
          className={`flex items-center justify-between rounded-2xl px-5 sm:px-7 py-3.5 transition-all duration-500 ${
            scrolled
              ? "glass-dark border border-white/10"
              : "bg-transparent border border-transparent"
          }`}
        >
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-home-link">
            <span
              className="font-display text-2xl font-black tracking-tight gradient-gold-text leading-none"
              aria-label="SS"
            >
              SS
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                Salama
              </span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/80">
                Solutions
              </span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-5 py-2.5 text-[15px] font-semibold tracking-wide transition-colors ${
                    isActive ? "text-agency-gold" : "text-white/70 hover:text-white"
                  }`
                }
                data-testid={`nav-link-${l.key}`}
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute left-3 right-3 -bottom-0.5 h-px bg-agency-gold"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-white/70 hover:text-agency-gold transition-colors border border-white/15 hover:border-agency-gold/50 rounded-full px-3 py-2"
              aria-label="Toggle language / تبديل اللغة"
              data-testid="navbar-language-toggle"
            >
              <Languages size={14} />
              {lang === "en" ? "AR" : "EN"}
            </button>
            <a
              href={contact.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="text-base text-white/70 hover:text-white transition-colors"
              data-testid="navbar-whatsapp-link"
            >
              {t.nav.whatsapp}
            </a>
            <Link
              to="/contact"
              className="text-xs uppercase tracking-[0.2em] bg-agency-gold text-black font-bold px-5 py-3 hover:bg-white transition-colors"
              data-testid="navbar-cta-contact"
            >
              {t.nav.cta}
            </Link>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleLanguage}
              className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.1em] text-white/70 border border-white/15 rounded-full px-2.5 py-1.5"
              aria-label="Toggle language / تبديل اللغة"
              data-testid="navbar-language-toggle-mobile"
            >
              <Languages size={12} />
              {lang === "en" ? "AR" : "EN"}
            </button>
            <button
              className="text-white p-2"
              onClick={() => setOpen((s) => !s)}
              aria-label="Toggle menu"
              data-testid="navbar-mobile-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-5 mt-2 rounded-2xl glass-dark border border-white/10 overflow-hidden"
          >
            <div className="flex flex-col p-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base border-b border-white/5 last:border-b-0 ${
                      isActive ? "text-agency-gold" : "text-white/80"
                    }`
                  }
                  data-testid={`mobile-nav-${l.key}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-3 text-center text-xs uppercase tracking-[0.2em] bg-agency-gold text-black font-bold px-5 py-3"
                data-testid="mobile-cta-contact"
              >
                {t.nav.cta}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
